import {
  WSCard,
  WSElement,
  WSField,
  WSFileInput,
  WSFlexBox,
  WSForm,
  WSInputText,
  WSList,
  WSSectionToolbar,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import { useWSQuery } from "@ws-react-query";
import { ChangeEvent, useCallback, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormDataAttachment } from ".";
import { useMemberPublicCreate } from "../../../../query/files/mutations";
import { filesService } from "../../../../services/files";
import { concurrentActions } from "../../../../utils/serviceHelper";

type Props = {
  notesInitialVisible?: boolean;
};

export const AdditionalDetails: React.FC<Props> = props => {
  const { register, control, trigger, setValue } = useFormContext();

  const [memberPublicCreate, memberPublicCreateMeta] = useMemberPublicCreate();
  const {
    fields: attachmentsField,
    append,
    remove,
    insert
  } = useFieldArray<FormDataAttachment>({
    control,
    name: "attachments"
  });

  const [notesVisible, setNotesVisible] = useState(
    props.notesInitialVisible || false
  );
  const visibleAttachments = attachmentsField.filter(
    attachment => attachment.fileId && !attachment.remove
  );

  const openFileUpload = useCallback(() => {
    document.getElementById("attachmentsFileUpload")?.click();
  }, []);

  return (
    <WSElement>
      <WSSectionToolbar title="Additional details (optional)" />

      <WSList gap="XL">
        <WSForm.Field
          hidden={!notesVisible}
          name="notes"
          label="Notes (optional)"
          component={WSInputText}
          componentProps={{
            action: {
              text: "Remove",
              onClick: () => {
                setValue("notes", "");
                setNotesVisible(false);
              }
            }
          }}
        />

        {attachmentsField.map((attachment, index: number) =>
          attachment.fileId ? (
            <>
              <input
                ref={register()}
                type="hidden"
                name={`attachments[${index}].remove`}
                defaultValue={attachment.remove}
              />
              <input
                ref={register()}
                type="hidden"
                name={`attachments[${index}].fileId`}
                defaultValue={attachment.fileId}
              />
            </>
          ) : null
        )}

        <WSElement hidden>
          <WSFileInput
            id="attachmentsFileUpload"
            name="attachmentsFileUpload"
            onChange={async (event: ChangeEvent<HTMLInputElement>) => {
              event.stopPropagation();
              event.preventDefault();
              if (event.target && event.target.files && event.target.files) {
                const file = event.target.files[0];
                const data = new FormData();
                data.append("file", file);
                await memberPublicCreate(data, {
                  onSuccess: async createdFiles => {
                    createdFiles.forEach(createdFile => {
                      append({
                        fileId: createdFile.fileId
                      });
                    });
                    // manually trigger validation for attachments
                    await trigger("attachments");
                  }
                });
              }
            }}
          ></WSFileInput>
        </WSElement>

        <WSElement>
          {visibleAttachments.length > 0 && (
            <>
              <WSField
                label="Attachments (optional)"
                action={{
                  text: "Add attachment",
                  onClick: () => {
                    openFileUpload();
                  }
                }}
              />

              <AttachmentsTable
                fileIds={attachmentsField
                  .filter(attachment => attachment.fileId && !attachment.remove)
                  .map(attachment => attachment.fileId as string)}
                onRemove={fileId => {
                  const index = attachmentsField.findIndex(
                    attachment => attachment.fileId === fileId
                  );
                  remove(index);
                  insert(index, { fileId, remove: "1" });
                  // manually trigger validation for attachments
                  trigger("attachments");
                }}
              />
            </>
          )}

          {memberPublicCreateMeta.isError && (
            <WSText.ParagraphSm color="garnet" my="M">
              Ooops! Something wrong with this file..
            </WSText.ParagraphSm>
          )}
        </WSElement>

        {(!notesVisible || visibleAttachments.length === 0) && (
          <WSFlexBox gap="L">
            {!notesVisible && (
              <WSCard
                onClick={() => {
                  setNotesVisible(true);
                }}
                header={{
                  value: {
                    action: {
                      icon: "note",
                      label: "Add notes"
                    }
                  }
                }}
              />
            )}

            {visibleAttachments.length === 0 && (
              <WSCard
                onClick={() => {
                  openFileUpload();
                }}
                header={{
                  value: {
                    action: {
                      icon: "paperclip",
                      label: "Add attachment"
                    }
                  }
                }}
              />
            )}
          </WSFlexBox>
        )}
      </WSList>
    </WSElement>
  );
};

type AttachmentsTableProps = {
  fileIds: string[];
  onRemove: (fileId: string) => void;
};

const AttachmentsTable: React.FC<AttachmentsTableProps> = props => {
  const query = useWSQuery(["INVOICE_ATTACHMENTS", props.fileIds], async () => {
    return await concurrentActions(
      props.fileIds.map(fileId => () => filesService.member.public.get(fileId))
    );
  });

  return (
    <WSTable
      tableData={(query.data || []).map(file => ({
        id: file.fileId,
        data: { filename: file.filename }
      }))}
      columns={[
        {
          renderFunction: row => <WSTableCell text={row.data.filename} />
        }
      ]}
      rowActions={row => [
        {
          text: "Remove",
          onClick: () => {
            props.onRemove(row.id);
          }
        }
      ]}
    />
  );
};
